/**
 * 决策日面板
 * 设计原型已归档（2026-10-24）：原基准 ui-concept/月度执行.html 底层页面；现行实现见本文件 + docs/UI规范.md §2.1/§3
 * 顶部状态栏 + 「下月行程安排」薄荷块（4 周排程）+ 行程类别 Tab + 可选行程列表 + 确认/保存
 */
import type { GameApp } from '../../app';
import { GameState } from '../../systems/GameState';
import { scheduleSystem } from '../../systems/ScheduleSystem';
import { awardSystem } from '../../systems/AwardSystem';
import { gameLoop } from '../../systems/GameLoop';
import { scriptSystem, scheduleDeadlineTextOf } from '../../systems/ScriptSystem';
import { EventBus, GameEvents } from '../../core/EventBus';
import { Utils } from '../../core/Utils';
import { GameData } from '../../data/GameData';
import { GameConfig } from '../../core/GameConfig';
import { AttributeKey, TagDef, FixedMedia } from '../../data/types';
import {
    attrName, createDiv, createSpan, createButton, createBtn,
    createSlot, createCatTab, showHint, createExecTopBar,
    type StatBarParts,
} from '../ui';
import { icon } from '../icons';
import { MediaPanel } from './MediaPanel';
import { PlotPlayer } from '../plotPlayback';

type Category = 'course' | 'work' | 'rest';

/** 行程选择条目：介绍板与三列格子共用一份数据 */
interface ActivityEntry {
    id: string;
    name: string;
    /** 格子第二行属性对/状态小标（基础行程从数据推导，见 refresh 内 tag 计算） */
    tag: string;
    desc: string;
    gains: string[];
    costs: string[];
    /** 路径信息（可选，部分行程有） */
    path?: string;
    clickable: boolean;
    pick?: () => void;
}

/** 试镜介绍（试镜为独立行程不入 Schedules 表，介绍存此；执行文案在 GameLoop，两处分离）。
 *  2026-10-27 改版「一次试镜行程只面一个剧组」：先在试镜周挑剧组，再逐角色面。 */
const AUDITION_DESC = '试镜周里挑一个剧组面试，把合适的角色都面一遍。';

const CATEGORY_LABELS: Record<Category, string> = { course: '训练', work: '工作', rest: '休息' };
/** 属性条满分（批3-A：查属性定义表，随 GameData 装载时的 ATTR_SCALE 预处理自动缩放，⛔ 不再写死 100） */
const ATTR_MAX = GameData.ATTRIBUTE_DEFS.talent.max;

export class DecisionPanel {
    readonly element: HTMLElement;

    private currentCategory: Category = 'course';
    /** 玩家手动点选的空位（-1 = 未点选）。只在该格仍为空时生效，填掉即自动回到顺序模式 */
    private pinnedWeek = -1;
    /** 点选所属月份（year*12+month）。跨月或读档后失配 → 点选作废，保证新月第一格永远是第 1 周 */
    private pinnedStamp = -1;

    // 可复用节点
    private dateBadge: HTMLElement;
    /** 公共顶栏（2026-10-04 全屏化抽公共：与执行页 1:1 共用一份实现，见 ui.ts createExecTopBar） */
    private topBar: import('../ui').ExecTopBar;
    private slotsTitle: HTMLElement;
    private slotRow: HTMLElement;
    private catTabs: HTMLElement;
    private activityList: HTMLElement;
    /** 详情卡（嵌在列表薄荷块顶部的白卡）：显示 selId 指向条目的介绍；未选中=收起不显示（2026-10-25 乙案：占位卡下架） */
    private introPanel: HTMLElement;
    /** 滑档（详情卡外壳）：收合尺照抄 .collapse-area 的 max-height 过渡；展开滑档高=卡 99，对下方格子的布局位移=99+块内缝 6 */
    private detailSlot: HTMLElement;
    /** 详情卡当前指向的条目 id（点格子=选中并排入；切页签/点槽位清空并收起卡） */
    private selId: string | null = null;
    private confirmBtn: HTMLButtonElement;

    /** 决策页剧情演出档的媒体弹窗（VN 对话框复用 AssistPanel 的实例——同一组件、同一位置） */
    private readonly mediaPanel = new MediaPanel();
    /** 演出档演完之后要接的动作（点确认行程链上的下一环＝开月度执行页）；本档没戏时不走这里、当场接 */
    private afterPlot: (() => void) | null = null;
    /**
     * 决策页这一片的播放器（场地＝`FixedEventDef.playOn === 'decision'`，播出侧那类特殊剧情）。
     * 排干判据与月度执行页、出行地图共用 `PlotPlayer`，这里只交代三张嘴：
     * VN 复用 AssistPanel 的对话框（位置/样式与平时决策页一致）、media 卡弹进本页的 MediaPanel、
     * 全演完接 `afterPlot`（＝开月度执行页）。
     */
    private readonly plotPlayer = new PlotPlayer('decision', {
        playLines: (lines, onEnd) => this.app.assistPanel.playLines(lines, onEnd),
        showMedia: media => this.showMediaVia(this.plotPlayer, media),
        onDrained: () => { const then = this.afterPlot; this.afterPlot = null; then?.(); },
    });
    /**
     * **决策页入场**那一档演出（宿主 `decisionEntry`，2026-10-22 用户令「每个月执行完行程之后在决策页触发，
     * 在每月的影视热度表之前」）：数值/舆论类事件住这儿 ⇒ 演完才轮到影视热度榜定格与弹榜。
     * 与 `plotPlayer`（宿主 `decision`＝点「确认行程」之后）**同一支媒体面板**，靠 `showMediaVia` 把
     * 「关掉卡片」这个回调绑给真正弹卡的那一档。
     */
    private readonly entryPlayer = new PlotPlayer('decisionEntry', {
        playLines: (lines, onEnd) => this.app.assistPanel.playLines(lines, onEnd),
        showMedia: media => this.showMediaVia(this.entryPlayer, media),
        onDrained: () => { const then = this.afterPlot; this.afterPlot = null; then?.(); },
    });
    /** 媒体面板只有一个 ⇒ 谁弹的卡，「知道了」就还给谁（两档演出不在同一时刻，绑定不会打架） */
    private showMediaVia(player: PlotPlayer, media: FixedMedia): void {
        this.mediaPanel.onDone = () => player.onMediaDone();
        this.mediaPanel.show(media);
    }

    /**
     * 品牌官宣媒体卡（2026-10-30 商务官宣改版）：签约次月决策页入场时由送达链调用，复用本页 MediaPanel。
     * 与剧情两档的区别只是绑定对象在 app 层（不在 PlotPlayer）——关掉卡即把控制权交回送达链。
     */
    showAnnounceMedia(media: FixedMedia, onDone: () => void): void {
        this.mediaPanel.onDone = onDone;
        this.mediaPanel.show(media);
    }

    constructor(private app: GameApp) {
        const panel = createDiv('panel-root');
        const inner = createDiv('decision-inner');

        // ===== 顶部状态栏 + 资源行 + 属性折叠区（公共顶栏，2026-10-04 抽公共与执行页共用） =====
        this.topBar = createExecTopBar();
        inner.appendChild(this.topBar.root);

        // ===== 下月行程安排薄荷块（65 批：三主页撤撕边+胶带，恢复四角圆角）=====
        this.slotsTitle = createSpan('mint-block-title', '');
        const slotBlock = createDiv('mint-block slot-block');
        slotBlock.appendChild(this.slotsTitle);
        this.slotRow = createDiv('slot-row');
        slotBlock.appendChild(this.slotRow);
        inner.appendChild(slotBlock);

        // ===== 分类页签 + 可选行程列表薄荷块（页签连体，2026-10-08：签贴在纸页顶部、
        // 选中签与薄荷块同纸无缝且浮在最前，未选中签被纸页上缘压住——文件夹索引签读法。
        // 包进同一容器才能建立签与块的层叠关系，⛔ 拆回两个游离兄弟） =====
        const pageWrap = createDiv('page-wrap');
        this.catTabs = createDiv('cat-tabs');
        pageWrap.appendChild(this.catTabs);
        const listBlock = createDiv('mint-block list-block');
        this.introPanel = createDiv('intro-panel');
        this.detailSlot = createDiv('detail-slot');
        this.detailSlot.appendChild(this.introPanel);
        this.activityList = createDiv('activity-list');
        listBlock.appendChild(this.detailSlot);
        listBlock.appendChild(this.activityList);
        pageWrap.appendChild(listBlock);
        inner.appendChild(pageWrap);

        // ===== 操作行 =====
        const btnRow = createDiv('btn-row');
        // 手动保存按钮已移除：Archive 由 GameLoop.finishMonth() 月末自动保存
        const clearBtn = createButton('btn btn-ghost', '清空');
        clearBtn.addEventListener('click', () => this.onClear());
        this.confirmBtn = createButton('btn btn-primary', '确认行程并进入下月');
        this.confirmBtn.appendChild(icon('chevronRight'));
        this.confirmBtn.addEventListener('click', () => this.onConfirm());
        btnRow.appendChild(clearBtn);
        btnRow.appendChild(this.confirmBtn);
        inner.appendChild(btnRow);

        panel.appendChild(inner);

        // ===== 剧情演出档（2026-10-14 茄子令「爆火剧情挪到点击确认行程之后、在决策页展示」）=====
        // VN 对话框复用 AssistPanel 的实例（挂在 game-shell 上，位置与平时决策页一致）；
        // media 弹窗不挂 inner：`refresh()` 每有状态变化就重排 inner 里的槽位与列表，
        // 演到一半被一次 STATE_CHANGED 重排会把卡吞掉（deliver 那一刻就发这个事件）。
        // 2026-10-16 弹窗统一批：既不挂 inner 也不再挂本面板根，改挂外壳（同热度榜/咖位阶梯）——
        // 面板根在 .panel-container 里，导航条是它无 z 的兄弟且 DOM 靠后，卡浮在 z10 也压不住底下那排按钮。
        this.app.overlayRoot.appendChild(this.mediaPanel.element);
        this.mediaPanel.onDone = () => this.plotPlayer.onMediaDone();

        this.element = panel;

        // 状态变化实时刷新
        EventBus.on(GameEvents.STATE_CHANGED, this.handleRefresh);
        EventBus.on(GameEvents.RESOURCE_CHANGED, this.handleRefresh);
        EventBus.on(GameEvents.RANK_CHANGED, this.handleRefresh);
    }

    private handleRefresh = (): void => { this.refresh(); };

    /** 槽位点击：已安排则取消这一周，空位则把下一次安排钉到这一周 */
    private onSlotClick(idx: number): void {
        this.selId = null;  // 点槽位清空选中
        const state = GameState.getInstance();
        const item = state.player.schedule[idx];
        if (item) {
            // 取消后它自然就是「第一个空位」，无需再钉
            state.player.schedule[idx] = null;
        } else {
            this.pinnedWeek = idx;
            this.pinnedStamp = state.player.year * 12 + state.player.month;
        }
        this.refresh();
    }

    private onCategoryClick(cat: Category): void {
        this.currentCategory = cat;
        this.selId = null;  // 切页签清空选中
        this.refresh();
    }

    /** 下一次「安排行程」落在哪一格：手动点选且仍为空的格子优先，否则按 1→4 取第一个空位；-1 = 已排满 */
    private targetWeek(): number {
        const p = GameState.getInstance().player;
        const w = this.pinnedWeek;
        if (w >= 0 && w < p.schedule.length && this.pinnedStamp === p.year * 12 + p.month && !p.schedule[w]) return w;
        return p.schedule.findIndex(s => !s);
    }

    /** 取下一格；已排满时提示并返回 -1（不再静默覆盖最后一周） */
    private reserveWeek(): number {
        const week = this.targetWeek();
        if (week < 0) showHint('四周已排满');   // 只报状态；「点格子可取消」由格子本身可点承担（2026-11-01 去教学句）
        return week;
    }

    /** 选择行程填入当前格，格子由 targetWeek() 推导，排完自动顺位到下一个空位 */
    private pickSchedule(scheduleId: string): void {
        const week = this.reserveWeek();
        if (week < 0) return;
        // 试镜 = kind:'audition' + refId='AUDITION' 的一周行程（不绑定剧本），走专用便捷方法
        if (scheduleId === 'AUDITION') {
            scheduleSystem.setAuditionSchedule(week);
        } else {
            scheduleSystem.setBasicSchedule(week, scheduleId);
        }
        this.refresh();
    }

    private onClear(): void {
        scheduleSystem.clearSchedule();
        this.pinnedWeek = -1;
        this.pinnedStamp = -1;
        this.refresh();
    }

    private onConfirm(): void {
        if (!scheduleSystem.isScheduleComplete()) return; // 按钮已禁用，仅做兜底
        gameLoop.confirmMonth();       // 先公布本届名单/结算本届颁奖夜（批 G），并置 week=1、进入执行态
        // 行程开始前先把"该给玩家看的话"给完：先小助理积压（含 12 月初的入围播报），再颁奖夜，再
        // 决策页剧情演出档（2026-10-14 茄子令：爆火那类剧情挪到这里），演完才开月度执行弹窗。
        // 月度执行弹窗属 MODAL_PANELS，一开就会把对话整段收起且不记已读（2026-09-30 抢屏修复 + 批 G）
        this.app.flushAssistantThen(() => this.playDecisionPlotThen(() => this.app.show('monthProgress')));
    }

    /**
     * 决策页剧情演出档（2026-10-14 茄子令「爆火剧情还是挪到点击确认行程之后，大概时间点就是在颁奖夜之后、
     * 行程执行之前，在决策页展示这个剧情」）：排在 `flushAssistantThen` 的续动作位，
     * 颁奖夜把 `done` 交回来的那一刻才轮得到它。队里没有本宿主的拍就直接接 `done`（＝开月度执行页）。
     * 热爆那条链的 message 拍不在这里演——月底 `sweepChatDelivery` 统一投递（茄子同日口径不变）。
     */
    private playDecisionPlotThen(done: () => void): void {
        this.afterPlot = done;
        if (this.plotPlayer.playNext()) return;
        this.afterPlot = null;
        done();
    }

    /** 决策页**入场**时有没有待演的事件（宿主 decisionEntry） */
    hasEntryEvents(): boolean {
        return this.entryPlayer.pendingCount() > 0;
    }

    /**
     * 决策页入场的**事件档**（数值/舆论类事件）：演完才继续入场链（影视热度榜定格 → 弹榜 → 小助理播报）。
     * 与 `playDecisionPlotThen`（确认行程后那档）同一套出口形状：把「接下来干什么」挂在 `afterPlot` 上。
     */
    playEntryEventsThen(done: () => void): void {
        this.afterPlot = done;
        if (this.entryPlayer.playNext()) return;
        this.afterPlot = null;
        done();
    }

    // ===== 渲染 =====

    refresh(): void {
        const state = GameState.getInstance();

        // 顶部：公共顶栏全量刷新（姓名/咖位/年月/体力金钱/属性条/标签）
        this.topBar.update(state);

        // 行程标题（month 即当前正在执行的月份：开局=第1年1月，规划本月行程）
        this.slotsTitle.textContent = `行程安排 · 第${state.player.year + 1}年${state.player.month}月`;

        // 4 周槽位：已安排显示行程名，空格一律「待安排」，落点格只靠 .target 描边浮起区分
        this.slotRow.innerHTML = '';
        const cur = this.targetWeek();
        for (let i = 0; i < 4; i++) {
            const item = state.player.schedule[i];
            const slot = createSlot(i, item ? (item.title ?? '已安排') : '待安排', item ? 'filled' : (i === cur ? 'target' : 'idle'));
            slot.addEventListener('click', () => this.onSlotClick(i));
            this.slotRow.appendChild(slot);
        }

        // 类别 Tab
        this.catTabs.innerHTML = '';
        (['course', 'work', 'rest'] as Category[]).forEach(cat => {
            const tab = createCatTab(CATEGORY_LABELS[cat], cat === this.currentCategory);
            tab.addEventListener('click', () => this.onCategoryClick(cat));
            this.catTabs.appendChild(tab);
        });

        // 行程列表（2026-10-01 起资源不足⛔ 置灰：排程期不扣资源、执行期逐周结算，
        // 先排休息/工作把体力金钱滚起来、后面周位照样够——可行性交玩家自己盘算，
        // 资源不够的周执行时由 ScheduleSystem 兜底改在家休息并播报）
        this.activityList.innerHTML = '';

        // ===== 收集当前分类的可选条目（介绍板与三列格子共用同一份数据） =====
        const entries: ActivityEntry[] = [];

        if (this.currentCategory === 'work') {
            // 置顶位：试镜（独立行程不入 Schedules 表，介绍文案存 AUDITION_DESC）
            entries.push({
                id: 'AUDITION', name: '试镜', tag: '进剧本池',
                desc: AUDITION_DESC,
                gains: ['挑一个剧组逐角色试镜'], costs: [`体力-${GameConfig.AUDITION_ENERGY_COST}`],
                clickable: true, pick: () => this.pickSchedule('AUDITION'),
            });

            // 已签约通告（本月剩余周数排满后隐藏；未开机=置灰待排）
            const active = state.player.contracts.filter(c => c.status === 'active');
            const absNow = state.player.year * 12 + state.player.month;
            const scheduledCount = new Map<string, number>();
            state.player.schedule.forEach(sc => {
                if (sc?.contractId) scheduledCount.set(sc.contractId, (scheduledCount.get(sc.contractId) ?? 0) + 1);
            });
            active.forEach(contract => {
                const used = scheduledCount.get(contract.id) ?? 0;
                // 日历化批：还差几周 = 应拍 − 已拍（读 shotWeeksDone），本月已排的也算进去
                const left = Math.max(0, scriptSystem.shotWeeksLeftOf(contract) - used);
                if (left <= 0) return; // 剩余周数已排满，隐藏
                const script = GameData.SCRIPT_MAP[contract.scriptId];
                if (!script) return;
                // 待开机（2026-10-27 滚动排期）：统一档期未到 = 置灰不可排，开机月自动解锁进组
                if (absNow < contract.shootStartAbs) {
                    const months = contract.shootStartAbs - absNow;
                    const sy = Math.floor((contract.shootStartAbs - 1) / 12);
                    const sm = contract.shootStartAbs - sy * 12;
                    entries.push({
                        id: contract.id,
                        name: `《${script.name}》`,
                        tag: `${months}个月后开机`,
                        desc: `第${sy + 1}年${sm}月开机。开机月前不可排，到时自动进组。`,
                        gains: [], costs: [`体力-${GameConfig.CONTRACT_ENERGY_COST}`], clickable: false,
                    });
                    return;
                }
                const deadlineText = scheduleDeadlineTextOf(contract, script, absNow);
                const gains: string[] = [];   // 剩余周数在 tag「拍摄·N周」已有，⛔ 收益胶囊再说一遍（2026-10-27 茄子令删）
                const costs = [`体力-${GameConfig.CONTRACT_ENERGY_COST}`];
                // 评奖窗口知情（剧组等主角批 B）：按当前节奏全剧播完月对照最近一届参评窗口
                const dl = awardSystem.awardDeadlineInfo(script, contract);
                if (dl?.status === 'missed') costs.push(`错过${dl.awardName}`);
                else if (dl?.status === 'tight') costs.push(`压线${dl.awardName}`);
                entries.push({
                    id: contract.id,
                    name: `《${script.name}》`,
                    tag: `拍摄·${left}周`,
                    desc: `进组拍摄的一周。这个角色${deadlineText}，逾期作废。`,
                    gains, costs, clickable: true,
                    pick: () => {
                        const week = this.reserveWeek();
                        if (week >= 0) scheduleSystem.setContractSchedule(week, contract.id, contract.scriptId);
                    },
                });
            });

            // 品牌活动（挚友，2026-11-02）：签约当月内自由排一周完成；逾期月末按合同扣违约金
            const pendingEvents = (state.player.endorsements ?? []).filter(c =>
                c.active && c.title === 'friend' && c.eventMonth === absNow);
            pendingEvents.forEach(c => {
                if (state.player.schedule.some(sc => sc?.endorseId === c.id)) return;
                const brand = GameData.BRAND_MAP[c.brandId];
                const brandName = brand?.name ?? c.brandId;
                entries.push({
                    id: c.id,
                    name: `${brandName}品牌活动`,
                    tag: '本月完成',
                    desc: '品牌活动的一周：出席、拍摄、采访，按品牌方的流程走完。本月内完成，逾期按合同扣违约金。',
                    gains: [`¥+${Utils.formatMoney(c.eventFee ?? 0)}`],
                    costs: [`体力-${GameConfig.CONTRACT_ENERGY_COST}`],
                    clickable: true,
                    pick: () => {
                        const week = this.reserveWeek();
                        if (week >= 0) scheduleSystem.setEndorseSchedule(week, c.id, brandName);
                    },
                });
            });
        }

        const schedules = scheduleSystem.getAvailableSchedules()
            .filter(s => s.category === this.currentCategory);

        schedules.forEach(s => {
            // 资源不足⛔ 置灰拦截：排程期不扣资源、执行期逐周结算，可行性交玩家盘算；
            // 排进去执行时不够的周由 ScheduleSystem 兜底改为在家休息并播报。
            // 格子第二行属性对小标：属性名按表序 + 金钱/名气后置；纯恢复类显示「体力回复」
            const parts = Object.keys(s.attributeGain).map(k => attrName(k as AttributeKey));
            if (s.moneyGain > 0) parts.push('金钱');
            if (s.fameGain > 0) parts.push('名气');
            const tag = parts.length ? parts.join('·') : (s.energyRestore > 0 ? '体力回复' : '');
            const gains = [
                ...Object.entries(s.attributeGain).map(([k, v]) => `${attrName(k as AttributeKey)}+${v}`),
                s.fameGain && s.fameGain > 0 ? `名气+${s.fameGain}` : '',
                s.moneyGain > 0 ? `¥+${Utils.formatMoney(s.moneyGain)}` : '',
                s.energyRestore > 0 ? `体力+${s.energyRestore}` : '',
            ].filter(Boolean);   // 数值变动统一序（2026-10-26 茄子令）：加在前扣在后，组内 属性→名气→金钱→体力，与执行页变动行同口径
            const costs = [
                s.moneyCost > 0 ? `¥-${Utils.formatMoney(s.moneyCost)}` : '',
                s.energyCost > 0 ? `体力-${s.energyCost}` : '',   // 消耗体力的行程统一亮「体力-25」（与试镜/合同格同口径）
            ].filter(Boolean);
            entries.push({
                id: s.id, name: s.name, tag,
                desc: s.intro, gains, costs,
                clickable: true, pick: () => this.pickSchedule(s.id),
            });
        });

        // ===== 详情卡（乙案 2026-10-25：未选中=收起不显示，选中=从块顶滑出；名字与胶囊同行、描述限两行） =====
        const selEntry = entries.find(e => e.id === this.selId) ?? null;
        if (!selEntry) {
            this.detailSlot.classList.remove('open');
        } else {
            const chips = [
                selEntry.path ? `<span class="chip path">${selEntry.path}</span>` : '',
                ...selEntry.gains.map(g => `<span class="chip gain">${g}</span>`),
                ...selEntry.costs.map(c => `<span class="chip cost">${c}</span>`),
            ].join('');
            this.introPanel.innerHTML = `
                <div class="intro-head"><span class="intro-name">${selEntry.name}</span><span class="i-stats">${chips}</span></div>
                <div class="intro-desc">${selEntry.desc}</div>`;
            this.fitIntroHead();
            this.detailSlot.classList.add('open');
        }

        // ===== 三列格子（点格子=排入+详情卡滑出指向；格子无常驻选中态，2026-10-25 乙案） =====
        entries.forEach(e => {
            const btn = createBtn(`activity-btn${e.clickable ? '' : ' dim'}`);
            btn.appendChild(createSpan('nm', e.name));
            btn.appendChild(createSpan('tag', e.tag));
            btn.addEventListener('click', () => {
                this.selId = e.id;
                e.pick?.();
                this.refresh();
            });
            this.activityList.appendChild(btn);
        });
        this.fitGridNames();

        // 下月行程未排满 4 周 → 确认按钮置灰不可点（实时随排程变化刷新）
        this.confirmBtn.disabled = !scheduleSystem.isScheduleComplete();
    }

    /** 格子名字单行锁死（2026-10-26 同策略第三处）：通告格剧名可超长，装不下逐级缩字号（步进 0.05、
     *  0.6 下限，比卡头松——格子窄），缩到下限仍溢出挂 .clamp 省略号兜底（格子名截断可接受：全名在详情卡）。 */
    private fitGridNames(): void {
        this.activityList.querySelectorAll<HTMLElement>('.activity-btn .nm').forEach(nm => {
            nm.classList.remove('clamp');
            nm.style.fontSize = '';
            let scale = 1;
            while (scale > 0.6 && nm.scrollWidth > nm.clientWidth) {
                scale -= 0.05;
                nm.style.fontSize = `calc(var(--fs-sm) * ${scale.toFixed(2)})`;
            }
            if (nm.scrollWidth > nm.clientWidth) nm.classList.add('clamp');
        });
    }

    /** 卡头一行锁死+介绍两行锁死（2026-10-26 手机窄屏令，同一策略两处消费）：装不下逐级缩基准字号
     *  （步进 0.04、0.72 下限）；卡头缩到下限仍溢出才挂 .wrap 兜底折行，介绍缩到下限仍超两行由
     *  line-clamp 省略号兜底（不剪字，只是收尾）。 */
    private fitIntroHead(): void {
        const head = this.introPanel.querySelector<HTMLElement>('.intro-head');
        if (head) {
            head.classList.remove('wrap');
            head.style.fontSize = '';
            let scale = 1;
            while (scale > 0.72 && head.scrollWidth > head.clientWidth) {
                scale -= 0.04;
                head.style.fontSize = `calc(var(--fs-md) * ${scale.toFixed(2)})`;
            }
            if (head.scrollWidth > head.clientWidth) head.classList.add('wrap');
        }
        const desc = this.introPanel.querySelector<HTMLElement>('.intro-desc');
        if (desc) {
            desc.style.fontSize = '';
            let scale = 1;
            while (scale > 0.72) {
                const lh = parseFloat(getComputedStyle(desc).lineHeight);
                if (desc.scrollHeight <= lh * 2 + 1) break;
                scale -= 0.04;
                desc.style.fontSize = `calc(var(--fs-sm) * ${scale.toFixed(2)})`;
            }
        }
    }

    destroy(): void {
        EventBus.off(GameEvents.STATE_CHANGED, this.handleRefresh);
        EventBus.off(GameEvents.RESOURCE_CHANGED, this.handleRefresh);
        EventBus.off(GameEvents.RANK_CHANGED, this.handleRefresh);
    }
}
